import React, { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  TextInput,
  CheckBox,
  TagsSelect,
  SelectInput,
  TextButton,
  RadioButtonsList,
  InlineInput,
} from "../../design-system/controls";
import type { TagOption as TagsSelectOption } from "../../design-system/controls/TagsSelect/TagsSelect";
import type { SelectOption as DSSelectOption } from "../../design-system/controls/SelectInput/SelectInput";
import {
  Controller,
  useForm,
  FormProvider,
  type FieldError,
} from "react-hook-form";
import "./IntegrationCustomFields.scss";
import { AlertIndicator, convertToCron } from "../../design-system/components";
import { alertTypes } from "../../design-system/components/AlertIndicator/AlertIndicator";
import type { SchedulerData } from "../../design-system/components/Scheduler/SchedulerUtils";
import { ConnectionStatusEnum } from "../DataSources/DataSourcesUtils";
import { useHistory } from "react-router-dom";
import { REQUIRED_ERROR_MESSAGE } from "../../utils";

const fieldTypesEnum = {
  STRING: "STRING",
  SECRET: "SECRET",
  HIDDEN: "HIDDEN",
  BOOL: "BOOL",
  LIST: "LIST",
  SELECT: "SELECT",
  SWITCH_TOGGLE: "SWITCH_TOGGLE",
  NUMBER: "NUMBER",
} as const;

const fieldSizesEnum = {
  SMALL: "SMALL",
  LARGE: "LARGE",
} as const;

type FieldInputType = (typeof fieldTypesEnum)[keyof typeof fieldTypesEnum];
type FieldInputSize = (typeof fieldSizesEnum)[keyof typeof fieldSizesEnum];

interface FieldOptionValue {
  id: string;
  name: string;
}

interface SwitchCase {
  label?: string;
  value: string;
  rows?: IntegrationRow[];
}

interface IntegrationFieldOptions {
  values?: FieldOptionValue[];
  switch_cases?: SwitchCase[];
}

export interface IntegrationField {
  field_name: string;
  field_input_type?: FieldInputType | string;
  field_input_size?: FieldInputSize | string;
  display_name?: string;
  default_string_value?: string;
  required?: boolean;
  disabled?: boolean | null;
  hidden?: boolean;
  is_readonly?: boolean;
  is_multi?: boolean;
  label_right?: string;
  min_value?: number;
  max_value?: number;
  tooltip?: string;
  options?: IntegrationFieldOptions;
}

export interface IntegrationRow {
  fields?: IntegrationField[];
}

export interface IntegrationSection {
  name?: string;
  rows?: IntegrationRow[];
}

interface IntegrationInstance {
  id?: string;
  fieldsValues?: { friendly_name?: string } & Record<string, unknown>;
}

interface CurrentIntegration {
  id?: string;
  instances?: IntegrationInstance[];
}

/** Value emitted by select-like controls. */
interface SelectOption {
  value: string;
  label: string;
}

/** Value emitted by the tags control. */
interface TagOption {
  id?: string;
  name?: string;
  value?: string;
}

/** Aggregated form values produced on submit. */
interface IntegrationFormData {
  enableScheduling?: boolean;
  schedule?: SchedulerData;
  selectedRapInstance?: { value: string };
  enableLastReportedThreshold?: boolean;
  enableLastCollectedThreshold?: boolean;
  maxNotSeenToClose?: number;
  lastCollectedThreshold?: number;
  incomingConfiguration?: Record<string, unknown>;
}

/** Payload submitted to the parent `OnSubmit` handler. */
interface IntegrationSubmitData {
  friendly_name?: unknown;
  days_not_seen_to_declare_fixed?: number;
  last_collected_threshold?: number;
  last_reported_threshold?: number;
  enable_last_reported_threshold?: boolean;
  enable_last_collected_threshold?: boolean;
  enabled?: boolean;
  cron?: string;
  rap_instance_id?: string;
  incoming_configuration?: string;
}

export interface IntegrationCustomFieldsProps {
  integrationDefinitionSections?: IntegrationSection[];
  fieldsValues?: Record<string, unknown>;
  submitBtnText?: string;
  shouldDisplayCancel?: boolean;
  integrationDefinitionId?: string;
  OnSubmit: (data: IntegrationSubmitData) => Promise<unknown>;
  currentIntegration?: CurrentIntegration;
  wrapperClassName?: string;
  disable?: boolean;
  useDisableFieldOnEdit?: boolean;
  connectionStatus?: string;
  children?: ReactNode;
  instanceId?: string;
  showLoaderOnSubmitButton?: boolean;
  isThreatIntelligence?: boolean;
}

const IntegrationCustomFields: React.FC<IntegrationCustomFieldsProps> = ({
  integrationDefinitionSections,
  fieldsValues,
  submitBtnText,
  shouldDisplayCancel = true,
  integrationDefinitionId,
  OnSubmit,
  currentIntegration,
  wrapperClassName,
  disable = false,
  useDisableFieldOnEdit = false,
  connectionStatus,
  children,
  instanceId,
  showLoaderOnSubmitButton = false,
  isThreatIntelligence = false,
}) => {
  const history = useHistory();
  const methods = useForm({ mode: "all", reValidateMode: "onChange" });
  const {
    handleSubmit,
    control,
    formState: { errors, isValid, isDirty },
    trigger,
  } = methods;

  useEffect(() => {
    if (instanceId) {
      trigger();
    }
  }, [instanceId, trigger]);

  const allInstancesNames = useMemo(
    () =>
      currentIntegration?.instances
        ?.filter((instance) => instance?.id !== instanceId)
        ?.map((instance) => {
          return instance?.fieldsValues?.friendly_name;
        }),
    [currentIntegration?.instances, instanceId]
  );

  const [listTypeFields, setListTypeFields] = useState<string[]>([]);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);

  const onSubmit = (formData: IntegrationFormData) => {
    setIsSubmittingForm(true);

    const {
      enableScheduling,
      schedule,
      selectedRapInstance,
      enableLastReportedThreshold,
      enableLastCollectedThreshold,
      maxNotSeenToClose,
      lastCollectedThreshold,
      incomingConfiguration,
    } = formData;

    const scheduleSettings: { enabled?: boolean; cron?: string } = {};

    if (enableScheduling !== undefined) {
      scheduleSettings.enabled = enableScheduling;
      // convert date object back to UTC cron.
      if (enableScheduling) {
        scheduleSettings.cron = convertToCron({
          schedulerData: schedule as SchedulerData,
        });
      }
    }

    let data: IntegrationSubmitData = {
      friendly_name: incomingConfiguration?.friendly_name,
      days_not_seen_to_declare_fixed: !!enableLastReportedThreshold
        ? maxNotSeenToClose
        : undefined,
      last_collected_threshold: !!enableLastCollectedThreshold
        ? lastCollectedThreshold
        : undefined,
      last_reported_threshold: !!enableLastReportedThreshold
        ? maxNotSeenToClose
        : undefined,
      enable_last_reported_threshold: enableLastReportedThreshold,
      enable_last_collected_threshold: enableLastCollectedThreshold,
      ...scheduleSettings,
    };
    delete incomingConfiguration?.friendly_name;

    if (!!selectedRapInstance) {
      data.rap_instance_id = selectedRapInstance.value;
    }

    const formattedFields = formatSpecialFieldsValue(
      incomingConfiguration ?? {}
    );

    data.incoming_configuration = JSON.stringify({
      ...incomingConfiguration,
      ...formattedFields,
    });

    OnSubmit(data).then(() => {
      setIsSubmittingForm(false);
    });
  };

  const generateSection = (section: IntegrationSection) => {
    return (
      <div
        className={"section"}
        key={`${integrationDefinitionId}-${section.name}`}
      >
        {section.name && (
          <div className={"section-title"}> {section.name} </div>
        )}
        {section.rows?.map((row) => generateRow(row))}
      </div>
    );
  };

  const generateRow = (row: IntegrationRow) => {
    return (
      <div className={"row"}>
        {row.fields?.map((field) => generateField(field, row.fields!.length))}
      </div>
    );
  };

  const generateField = (field: IntegrationField, numOfFieldsInRow: number) => {
    if (
      field.field_input_type === fieldTypesEnum.LIST &&
      !listTypeFields.includes(field.field_name)
    ) {
      setListTypeFields((oldListTypeFields) => [
        ...oldListTypeFields,
        field.field_name,
      ]);
    }

    const isFriendlyName = field?.field_name === "friendly_name";
    // according to the specs in https://betterviewcloud.atlassian.net/browse/WAK-21019
    // in TS- we should disable field editing if field.disabled when disabled: null
    // in DS- we should enable field editing if field.disabled is not set
    const disabledFieldOnEdit =
      useDisableFieldOnEdit && !!instanceId && (field?.disabled ?? true);

    return (
      <div
        key={field.field_name}
        className={
          field.field_input_type === fieldTypesEnum.HIDDEN || field?.hidden
            ? `hidden-field`
            : numOfFieldsInRow === 1 &&
                field.field_input_size === fieldSizesEnum.SMALL
              ? "field-s"
              : "field-l"
        }
      >
        <Controller
          name={`incomingConfiguration.${field.field_name}`}
          shouldUnregister={true}
          defaultValue={getFieldDefaultValueByType(field)}
          render={({ field: { value, onChange, onBlur } }) => {
            return generateInputElementByType(
              field,
              value,
              onChange,
              onBlur,
              disable || disabledFieldOnEdit
            );
          }}
          control={control}
          rules={{
            required:
              Boolean(field.required) && !(disable || disabledFieldOnEdit)
                ? REQUIRED_ERROR_MESSAGE
                : "",
            validate: isFriendlyName
              ? (value) =>
                  !allInstancesNames?.includes(value) || "Name already exists"
              : () => true,
          }}
        />
      </div>
    );
  };

  const formatSpecialFieldsValue = (fields: Record<string, unknown>) => {
    let formattedFields: Record<string, unknown> = {};

    Object.keys(fields).forEach((key) => {
      const fieldValue = fields[key];
      if (Array.isArray(fieldValue)) {
        formattedFields[key] = fieldValue
          .map((obj: TagOption) => (obj.name ? obj.name : obj.value))
          .join(",");
      } else if (typeof fieldValue === "object" && fieldValue !== null) {
        formattedFields[key] = (fieldValue as { value?: unknown }).value;
      }
    });

    return formattedFields;
  };

  const getFieldDefaultValueByType = (field: IntegrationField): unknown => {
    let value =
      !!fieldsValues && Object.keys(fieldsValues).includes(field.field_name)
        ? fieldsValues[field.field_name]
        : field.default_string_value;

    switch (field.field_input_type) {
      case fieldTypesEnum.BOOL: {
        return (typeof value === "boolean" && value) || value === "TRUE";
      }
      case fieldTypesEnum.LIST: {
        return value
          ? (value as string)?.split(",").map((tag) => {
              return { name: tag, id: tag };
            })
          : [];
      }
      case fieldTypesEnum.SELECT: {
        let selected: SelectOption[] = [];

        (value as string).split(",").forEach((selectedID) => {
          const selectedOption = field.options?.values?.find(
            (opt) => opt.id === selectedID
          );

          if (!!selectedOption) {
            selected.push({
              value: selectedOption.id,
              label: selectedOption.name,
            });
          }
        });

        if (!!selected.length) {
          return selected;
        } else {
          return;
        }
      }
      case fieldTypesEnum.SWITCH_TOGGLE: {
        if (value === "") {
          return field.options?.switch_cases?.[0]?.value;
        }
      }
      default:
        return value;
    }
  };

  const generateToggleSelection = (
    field: IntegrationField,
    selection: string
  ) => {
    if (!!selection) {
      const selectionObject = field?.options?.switch_cases?.find(
        (opt) => opt.value === selection
      );

      if (!!selectionObject) {
        return generateSection({ rows: selectionObject.rows });
      }
    }
  };

  const generateInputElementByType = (
    field: IntegrationField,
    value: unknown,
    onChange: (...event: unknown[]) => void,
    onBlur: () => void,
    disabled: boolean
  ) => {
    const fieldErrors = errors as Record<string, FieldError | undefined> & {
      incomingConfiguration?: Record<string, FieldError | undefined>;
    };
    switch (field.field_input_type) {
      case fieldTypesEnum.SWITCH_TOGGLE: {
        return (
          <>
            <RadioButtonsList
              options={field.options?.switch_cases}
              name={field.field_name}
              onBlur={onBlur}
              onChange={(val: unknown) => {
                onChange(val);
              }}
              value={value}
              disabled={disabled}
            />
            {generateToggleSelection(field, value as string)}
          </>
        );
      }
      case fieldTypesEnum.LIST: {
        return (
          <TagsSelect
            onChange={(tags: TagsSelectOption[]) => onChange(tags)}
            // value is RHF-controlled (unknown at this boundary); shape matches TagsSelect at runtime
            value={value as unknown as TagsSelectOption[]}
            allowNew
            suggestions={(value ? value : []) as unknown as TagsSelectOption[]}
            label={field.display_name}
            error={
              fieldErrors[field.field_name]
                ? fieldErrors[field.field_name]
                : null
            }
            labelTop
            onBlur={onBlur}
            minQueryLength={1}
            name={field.field_name}
            data-testid={field.display_name}
            newTagTextPrefix={"Create"}
            disabled={disabled}
            required={Boolean(field.required)}
          />
        );
      }
      case fieldTypesEnum.BOOL: {
        return (
          <CheckBox
            data-testid={field.field_name}
            checked={Boolean(value)}
            label={field.display_name}
            name={field.field_name}
            onChange={(val: unknown, newValue: unknown) => {
              onChange(newValue);
            }}
            disabled={disabled}
            required={Boolean(field.required)}
          />
        );
      }
      case fieldTypesEnum.SELECT:
        const selectOptions = field.options?.values.map((option) => {
          return { value: option?.id, label: option?.name };
        });
        return (
          <SelectInput
            data-testid={field.field_name}
            noMargin
            name={field.field_name}
            value={value as DSSelectOption | DSSelectOption[] | null | undefined}
            disabled={field.is_readonly || disabled}
            onBlur={onBlur}
            labelTop
            setDefaultSingleOption={true}
            onChange={(val: unknown) => onChange(val)}
            label={field.display_name}
            options={selectOptions}
            isMulti={field.is_multi}
            required={Boolean(field.required)}
          />
        );
      case fieldTypesEnum.NUMBER: {
        return (
          <InlineInput
            type={"number"}
            // RHF value is numeric/string at runtime; InlineInput renders it via the input value attr
            value={value as string}
            data-testid={field.field_name}
            onBlur={onBlur}
            name={field.field_name}
            noMargin
            onChange={(e: unknown) => onChange(e)}
            label={field.display_name}
            disabled={disabled}
            required={Boolean(field.required)}
            error={
              fieldErrors[field.field_name]
                ? fieldErrors[field.field_name]
                : null
            }
            labelRight={field.label_right}
            min={field.min_value}
            max={field.max_value}
            withHelp={field.tooltip}
            hideLabelHelper
          />
        );
      }

      default: {
        return (
          <TextInput
            type={"text"}
            value={value as string | number}
            labelTop
            data-testid={field.field_name}
            onBlur={onBlur}
            name={field.field_name}
            noMargin
            onChange={(e: unknown) => onChange(e)}
            label={field.display_name}
            disabled={disabled}
            required={Boolean(field.required)}
            error={
              fieldErrors?.incomingConfiguration?.[field.field_name]
                ? fieldErrors?.incomingConfiguration?.[field.field_name]
                : null
            }
          />
        );
      }
    }
  };

  return (
    <div className={"integration-fields-wrap"}>
      {connectionStatus === ConnectionStatusEnum.FAILED && (
        <AlertIndicator
          hasMaxWidth={false}
          type={alertTypes.ERROR}
          wrapperClassName={"connection-alert"}
          title={`Unable to establish a connection`}
          content={`Please check your credentials and try again. If you are unable to resolve the issue, please contact your customer success representative.`}
        />
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {integrationDefinitionSections?.map((section) =>
            generateSection(section)
          )}

          {children}

          {!disable && (
            <div
              className={`integration-fields-buttons ${
                wrapperClassName ? wrapperClassName : ""
              }`}
            >
              {shouldDisplayCancel && (
                <TextButton
                  type="button"
                  data-testid={"integration-custom-fields-cancel"}
                  label={"Cancel"}
                  onClick={() => {
                    if (currentIntegration?.instances?.length) {
                      history.push(
                        `/settings/integrations/${isThreatIntelligence ? "threatIntelligence" : "dataSources"}/${currentIntegration?.id}`
                      );
                    } else {
                      history.push(
                        `/settings/integrations/${isThreatIntelligence ? "threatIntelligence" : "dataSources"}`
                      );
                    }
                  }}
                />
              )}

              <TextButton
                data-testid={"user-actions-submit"}
                type="submit"
                disabled={
                  !isDirty ||
                  isSubmittingForm ||
                  showLoaderOnSubmitButton ||
                  !isValid
                }
                label={submitBtnText}
                buttonStyle={"primary"}
                rightIcon={
                  isSubmittingForm || showLoaderOnSubmitButton
                    ? "seem-icon-spinner spinner"
                    : ""
                }
                isSubmitting={isSubmittingForm}
              />
            </div>
          )}
        </form>
      </FormProvider>
    </div>
  );
};
export default IntegrationCustomFields;
